import { DEFAULT_PARTNER } from "./definePartner";
import type { Partner } from "./types";
import bluetokai from "./bluetokai";
import dotpe from "./dotpe";
import easyrewardz from "./easyrewardz";
import ewards from "./ewards";
import exclusife from "./exclusife";
import fudr from "./fudr";
import mloyal from "./mloyal";
import reelo from "./reelo";
import snaplion from "./snaplion";
import xeno from "./xeno";

/**
 * The partner registry: `metaId` → definition, one entry per partner folder
 * (docs/architecture/discounts/partners.md §2; DISCOUNT_V2_PLAN.md §9.12, R2.2).
 */
export const PARTNERS: Readonly<Record<string, Partner>> = Object.fromEntries(
  [xeno, bluetokai, dotpe, easyrewardz, exclusife, fudr, reelo, ewards, mloyal, snaplion].map((partner) => [
    partner.metaId,
    partner,
  ]),
);

/**
 * The definition for a partner.
 *
 * 🔴 **Never throws and never returns undefined.** An unknown, absent or newly enabled `metaId`
 * answers `DEFAULT_PARTNER`, the plain-coupon shape, so a partner nobody has written a folder for
 * still gets a working, conservative flow.
 */
export const getPartner = (metaId?: string): Partner => (metaId && PARTNERS[metaId]) || DEFAULT_PARTNER;

/** Every registered partner, for the contract suite and tooling. Excludes the baseline. */
export const registeredPartners = (): Partner[] => Object.values(PARTNERS);

export { DEFAULT_PARTNER, definePartner } from "./definePartner";
export type { PartnerDeclaration } from "./definePartner";
export { defaultSteps, getReferenceIdToStore } from "./steps";
export type {
  ApplyPlan,
  ApplyRequest,
  PartnerSteps,
  RequestContext,
  UnblockRequest,
  VerifyOutcome,
  VerifyRequest,
} from "./steps";
export type {
  Partner,
  PartnerBackend,
  PartnerConversation,
  PartnerDefinition,
  ReferenceIdSource,
  VerifyChannel,
  VerifySuccessSignal,
} from "./types";
